import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { clampRound, coerceNumber, evalExpression, getInstancePrefix } from '../../utils/autofill-math';
import type { FetchFormResponse } from '../../service/types';
import type { Question } from '../../config/types';

/** Non-empty, non-numeric => invalid */
function isNumericLike(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v !== 'string') return false;
  const s = v.replace(/,/g, '').trim();
  if (s === '') return true;
  return !Number.isNaN(Number(s));
}

/* ---------- component ---------- */
type Props = {
  question: Question;
  answers?: FetchFormResponse['answers'];
};

export default function AutoFillMath({ question, answers }: Props) {
  const { autofillMath } = question;
  const methods = useFormContext<FetchFormResponse['answers']>();
  if (!autofillMath) return null;

  // recalc continuously
  const when: 'always' | 'empty' = autofillMath.when ?? 'always';

  // Watch entire form so we always see vals
  const allValues = useWatch({ control: methods.control }) as Record<string, unknown> | undefined;

  React.useEffect(() => {
    const targetName = question.name;
    const prefix = getInstancePrefix(targetName);

    // Current RHF keys (after registration)
    const keys = Object.keys(allValues ?? methods.getValues() ?? {});
    // console.log('[AFM] keys:', keys);

    // Resolve RHF names for each alias
    const fullByAlias: Record<string, string> = {};
    for (const [alias, base] of Object.entries(autofillMath.fields)) {
      let full: string | undefined;
      if (prefix) {
        full = keys.find((k) => k.startsWith(prefix) && k.endsWith(base)) ?? `${prefix}${base}`;
      } else {
        full = keys.find((k) => k === base) || keys.find((k) => k.endsWith(`-${base}`)) || base;
      }
      fullByAlias[alias] = full!;
    }

    const sourceNames = Object.values(fullByAlias);
    if (!sourceNames.length) return;

    if (when === 'empty') {
      const { isDirty } = methods.getFieldState(targetName);
      if (isDirty) return;
    }

    // Build the resolved value map:
    // - If live value exists (including ''), use it ('' will coerce to 0).
    // - Only fall back to prior saved answer if the live value is truly undefined/null.
    const resolved: Record<string, unknown> = {};
    for (const [alias, full] of Object.entries(fullByAlias)) {
      const live = (allValues ?? {})[full];
      resolved[alias] = live !== undefined && live !== null ? live : answers?.[full];
    }

    // If invalid show blank
    if (Object.values(resolved).some((v) => !isNumericLike(v))) {
      if (methods.getValues(targetName) !== '') {
        methods.setValue(targetName, '', { shouldDirty: false, shouldValidate: true });
      }
      return;
    }
    const vars: Record<string, number> = {};
    for (const [alias, v] of Object.entries(resolved)) vars[alias] = coerceNumber(v);

    const raw = evalExpression(autofillMath.expression, vars);
    if (!Number.isFinite(raw)) return;

    const next = String(clampRound(raw, autofillMath.round, autofillMath.min, autofillMath.max));
    const current = methods.getValues(targetName);
    if (String(current ?? '') !== next) {
      methods.setValue(targetName, next, { shouldDirty: false, shouldValidate: true });
    }
  }, [
    JSON.stringify(allValues ?? {}), // recompute on any form change
    JSON.stringify(autofillMath), // recompute if expression/fields/min/max/round/when changes
    JSON.stringify(answers ?? {}), // historic fallback (optional)
    question.name, // target name (handles instance rows)
    methods,
  ]);

  return null;
}
